import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  fontStacks,
  palette,
} from '@munifocus/ui';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { SHORTCUTS } from '../lib/shortcuts';

function Key({ children }: { children: string }) {
  return (
    <Box
      component="kbd"
      sx={{
        fontFamily: fontStacks.mono,
        fontSize: 12,
        minWidth: 24,
        px: 0.75,
        py: 0.25,
        textAlign: 'center',
        borderRadius: 1,
        border: `1px solid ${palette.border.line}`,
        backgroundColor: palette.background.default,
        color: palette.text.primary,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * The keyboard shortcut overlay, opened with `?` or from the palette.
 *
 * Lists the global shortcuts, then those that act on the open file, so an
 * officer learns them where they will use them rather than from a manual.
 */
export function ShortcutHelpDialog({
  open,
  onClose,
  paletteShortcut,
}: {
  open: boolean;
  onClose: () => void;
  /** The platform's palette chord, e.g. "⌘K" or "Ctrl K". */
  paletteShortcut: string;
}) {
  const { t } = useTranslation();
  const sections = [
    { key: 'global', items: SHORTCUTS.filter((s) => !s.fileOnly) },
    { key: 'file', items: SHORTCUTS.filter((s) => s.fileOnly) },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="shortcut-help">
      <DialogTitle id="shortcut-help">{t('gov.shortcuts.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          {sections.map((section) => (
            <Box key={section.key}>
              <Typography variant="overline" sx={{ color: palette.text.secondary }}>
                {t(`gov.shortcuts.sections.${section.key}`)}
              </Typography>
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  rowGap: 1,
                  columnGap: 2,
                  alignItems: 'center',
                }}
              >
                {section.key === 'global' && (
                  <>
                    <Typography component="dt" variant="body2">
                      {t('gov.shortcuts.items.paletteChord')}
                    </Typography>
                    <Box component="dd" sx={{ m: 0, display: 'flex', gap: 0.5 }}>
                      <Key>{paletteShortcut}</Key>
                    </Box>
                  </>
                )}
                {section.items.map((s) => (
                  <Fragment key={s.id}>
                    <Typography component="dt" variant="body2">
                      {t(`gov.shortcuts.items.${s.id}`)}
                    </Typography>
                    <Box
                      component="dd"
                      sx={{ m: 0, display: 'flex', gap: 0.5, alignItems: 'center' }}
                    >
                      {s.keys.map((k, i) => (
                        <Fragment key={`${s.id}-${i}`}>
                          {i > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {t('gov.shortcuts.then')}
                            </Typography>
                          )}
                          <Key>{k.toUpperCase() === k ? k : k.toUpperCase()}</Key>
                        </Fragment>
                      ))}
                    </Box>
                  </Fragment>
                ))}
              </Box>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            {t('gov.shortcuts.typingHint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
